export const generateSanitizedRecords = (columnDefs: any[], currRow: any) => {
  const sanitizedRow: any = {};
  columnDefs.forEach(col => {
      if (col.valueGetter) {
        sanitizedRow[col.headerName] = col.valueGetter({ data: currRow, colDef: col}) ?? null;
      } else {
        sanitizedRow[col.headerName] = currRow[col.field] ?? null;
      }
  })
  return sanitizedRow;
}

/**
 * Build sanitized export rows from what the leader currently sees in the grid
 * (DELIV-21242): the selected rows that are on screen, in on-screen order; else
 * ag-Grid filtered+sorted rows, falling back to the full record list when the grid
 * is not ready. A selected row that a filter hides is not exported.
 * Columns flagged `excludeFromExport` are dropped, matching panel-sl-scorer-stats.
 */
export const buildFilteredExportRows = (gridApi: any, columnDefs: any[], fallbackRecords: any[], options: { useSelection?: boolean } = {}) => {
  // A grid whose selection only opens a detail panel or an editor (single selection) must not
  // narrow the export to the one row being looked at: it passes useSelection: false.
  const useSelection = options?.useSelection !== false;
  const exportedColumnDefs = (columnDefs || []).filter(col => !(<any>col)?.excludeFromExport);
  let exportSource: any[] = fallbackRecords || [];
  if (gridApi) {
    const filtered = [];
    const selectedOnScreen = [];
    gridApi.forEachNodeAfterFilterAndSort?.(node => {
      if (!node?.data) return;
      filtered.push(node.data);
      if (useSelection && node.isSelected?.()) selectedOnScreen.push(node.data);
    });
    // A grid with rows and a filter that leaves none exports nothing; only a grid
    // that has not loaded rows yet falls back to the records.
    let isGridLoaded = false;
    gridApi.forEachNode?.(node => { if (node?.data) isGridLoaded = true; });
    if (selectedOnScreen.length > 0) {
      exportSource = selectedOnScreen;
    } else if (isGridLoaded || exportSource.length === 0) {
      exportSource = filtered;
    }
  }
  return exportSource.map(r => generateSanitizedRecords(exportedColumnDefs, r));
}

/** What the Export button is given. Either a grid-backed source (columnDefs bound) or legacy ready-made rows. */
export interface ExportSource {
  gridApi?: any;
  columnDefs?: any[] | null;
  records?: any[] | null;
  /** Legacy: rows the panel built itself. Used only when `columnDefs` is not bound. */
  tableContents?: any[] | null;
  /** False when selecting a row opens a detail panel or an editor rather than choosing rows to export. Default true. */
  useSelection?: boolean;
}

/**
 * The one definition of what an Export button downloads, decided when the button is pressed.
 *
 * - With `columnDefs` bound: what the leader sees (see buildFilteredExportRows) - the selected rows
 *   on screen, else the filtered and sorted rows, else all `records` until the grid has loaded rows.
 *   A filter that hides every row therefore exports nothing.
 * - Without `columnDefs`: the panel's own `tableContents`, unchanged (legacy panels not moved yet).
 *
 * Always returns an array and never changes its inputs, so a missing grid, a missing list or a
 * non-array value produces "nothing to export" rather than an exception.
 */
export const resolveExportRows = (source: ExportSource | null | undefined): any[] => {
  if (!source) return [];
  if (Array.isArray(source.columnDefs)) {
    const records = Array.isArray(source.records) ? source.records : [];
    return buildFilteredExportRows(source.gridApi, source.columnDefs, records, { useSelection: source.useSelection });
  }
  return Array.isArray(source.tableContents) ? source.tableContents : [];
}

/**
 * ag-Grid 25 drops the selection whenever rowData is replaced (Refresh Data, Block/Unblock,
 * record filters). This remembers selected rows by identity and reselects them on the new
 * nodes. Rows a record filter removed stay remembered, so they come back selected when the
 * filter is cleared.
 *
 * Fed from `rowSelected`, not `selectionChanged`: API listeners are async, so a queued
 * selectionChanged can run after the rows were replaced and would read the new, unselected
 * nodes as a deselection. A rowSelected event carries the node it was raised for.
 */
export const createGridSelectionMemory = (rowKey: (data: any) => string) => {
  const selectedKeys = new Set<string>();
  return {
    onRowSelected(node: any) {
      if (!node?.data) return;
      const key = rowKey(node.data);
      // A row with no identity cannot be told apart from the next one, so it is never remembered.
      if (!key) return;
      if (node.isSelected?.()) selectedKeys.add(key);
      else selectedKeys.delete(key);
    },
    onRowDataChanged(gridApi: any) {
      if (!gridApi || selectedKeys.size === 0) return;
      gridApi.forEachNode?.(node => {
        if (!node?.data || node.isSelected?.()) return;
        const key = rowKey(node.data);
        if (key && selectedKeys.has(key)) node.setSelected(true);
      });
    },
    get size() { return selectedKeys.size; },
  };
}
